import { computed, ref, watch } from 'vue'
import { useWatchlistStore } from '@/stores'

const PER_PAGE = 40
const DEFAULT_SORT = { field: 'title', direction: 'asc' }

// Results table sort column -> GET /watchlists/:id/results `sort` query param. Country has no
// equivalent on this endpoint (not one of the fields the backend's merge/sort supports), so it's
// left out here rather than sent as a value the backend would 422 on.
const SORT_FIELDS = {
  'title': 'title',
  'mah_name': 'mah_name',
  'atc.code': 'atc_code',
  'registered_presentations': 'registered_presentations',
  'max_irp_presentations': 'max_irp_presentations',
  'reimbursed_presentations': 'reimbursed_presentations',
  'ma_status': 'ma_status',
  'on_market': 'on_market'
}

/**
 * The Results tab: a merged, paginated list of the watchlist's saved filter matches UNION
 * everything explicitly tracked (pinned brands/presentations, fav-group members), computed
 * server-side by GET /watchlists/:id/results. Reflects only the watchlist's last-SAVED
 * state — unlike the old InstantSearch-driven Results, it has no live preview of unsaved
 * criteria edits (see useWatchlistCriteria.js); that's a deliberate simplification, since the
 * merge/sort/cap logic now lives server-side with no concept of an unsaved draft.
 */
export const useWatchlistResults = ({ watchlistId }) => {
  const { indexResults } = useWatchlistStore()

  const sortField = ref(DEFAULT_SORT.field)
  const sortDirection = ref(DEFAULT_SORT.direction)

  const query = indexResults({
    watchlistId,
    sort: computed(() => SORT_FIELDS[sortField.value] ?? DEFAULT_SORT.field),
    direction: sortDirection,
    perPage: PER_PAGE
  })

  const rows = computed(() => query.data.value?.pages.flatMap(page => page.data) ?? [])
  const meta = computed(() => query.data.value?.pages[0]?.meta ?? null)
  const total = computed(() => meta.value?.total ?? 0)
  const isCapped = computed(() => !!meta.value?.capped)

  const loadNextPage = () => {
    if (!query.hasNextPage.value || query.isFetchingNextPage.value) return
    query.fetchNextPage()
  }

  const setSort = ({ field, direction } = {}) => {
    sortField.value = field && SORT_FIELDS[field] ? field : DEFAULT_SORT.field
    sortDirection.value = direction ?? DEFAULT_SORT.direction
  }

  // resetKey bumps on sort change so DataGrid resets its virtualizer to the top of the new order.
  const resetKey = ref(0)
  watch([sortField, sortDirection], () => {
    resetKey.value++
  })

  return {
    rows,
    total,
    isPending: query.isPending,
    isCapped,
    loadNextPage,
    resetKey,
    sortField,
    sortDirection,
    setSort,
    sortableFields: Object.keys(SORT_FIELDS)
  }
}
